<script setup lang="ts">
/**
 * Modale « Récompenses » : coffres de progression (niveau / rang militaire) NON
 * ouverts, cumulables. À l'ouverture, le butin est révélé sous forme de CARTES
 * face cachée que le joueur RETOURNE d'un clic : brillance par rareté au survol,
 * éclat + confettis à la révélation (amplifiés au-dessus de Rare). Un bouton
 * « Suivant » enchaîne le coffre suivant. Les objets gagnés rejoignent
 * l'inventaire (badge « New »). Butin tiré 100 % aléatoire (cf RewardChestService).
 */
import { computed, reactive, ref, watch } from 'vue';
import { ElMessage } from 'element-plus';
import { useOverlaysStore } from '@/stores/overlays.store';
import { useRewardChestsStore } from '@/stores/rewardChests.store';
import { useInventoryNewStore } from '@/stores/inventoryNew.store';
import { toDisplayItem, type DisplayItem } from '@/types/itemDisplay';
import { shipComponentToDisplayItem } from '@/types/shipComponent';
import type { RewardChest, RewardChestCard } from '@/api/rewardChest';
import ItemTooltipContent from '@/components/items/ItemTooltipContent.vue';

/** Logo du jeu affiché au dos des cartes (fond étoilé). */
const CARD_BACK_LOGO = '/images/logo-1.png';

const overlays = useOverlaysStore();
const store = useRewardChestsStore();
const inventoryNew = useInventoryNewStore();

const open = computed({
  get: () => overlays.rewardChestsOpen,
  set: (v: boolean) => { overlays.rewardChestsOpen = v; },
});

const opening = ref<number | null>(null);
const revealNonce = ref(0);
/** Image SDXL du coffre dispo ? sinon repli emoji (asset à générer). */
const chestImgOk = ref(true);
const CHEST_IMG = '/images/thumb/128/items/coffre_recompense.png';
/** Carte affichée pendant la révélation (+ état retourné). */
interface RevealCard extends RewardChestCard { flipped: boolean; key: string; }
const revealCards = reactive<RevealCard[]>([]);
const revealedChestType = ref<'Level' | 'Military' | 'Excavation' | null>(null);
const revealedTier = ref(0);

watch(open, (v) => { if (v) { resetReveal(); void store.refresh(); } });

function resetReveal(): void {
  revealCards.splice(0, revealCards.length);
  revealedChestType.value = null;
}
const inReveal = computed(() => revealCards.length > 0);
const allFlipped = computed(() => revealCards.every((c) => c.flipped));
const hasMore = computed(() => store.chests.length > 0);

function chestTitle(c: RewardChest): string {
  if (c.chestType === 'Military') return `Coffre de rang militaire (palier ${c.tier})`;
  if (c.chestType === 'Excavation') return `Coffre de ruine (niveau ${c.tier})`;
  return `Coffre de niveau ${c.tier}`;
}
function chestIcon(c: RewardChest): string {
  if (c.chestType === 'Military') return '🎖️';
  if (c.chestType === 'Excavation') return '🏺';
  return '⭐';
}
const revealTitle = computed(() => {
  if (revealedChestType.value === 'Military') return `Coffre de rang militaire ${revealedTier.value} ouvert !`;
  if (revealedChestType.value === 'Excavation') return `Coffre de ruine niveau ${revealedTier.value} ouvert !`;
  return `Coffre de niveau ${revealedTier.value} ouvert !`;
});

/** Mappe une carte de butin vers le DisplayItem commun en RÉUTILISANT les mappers
 *  d'inventaire : objet -> toDisplayItem (bonus + affixes + libellés/%/sens),
 *  composant -> shipComponentToDisplayItem (stats de base + affixes). Le tooltip
 *  (ItemTooltipContent) est donc STRICTEMENT identique à l'inventaire. Flouz /
 *  ressource = carte simple sans stats. */
function toDisplay(card: RewardChestCard): DisplayItem {
  if (card.item) return toDisplayItem(card.item);
  if (card.component) return shipComponentToDisplayItem(card.component);
  return {
    id: card.imageCode ?? card.label,
    code: card.imageCode ?? '',
    imageCode: card.imageCode ?? undefined,
    imageFolder: card.imageFolder,
    name: card.label,
    description: card.description ?? undefined,
    kind: null,
    rarity: card.rarity,
    quantity: card.quantity,
    stats: [],
  };
}
function thumb(card: RewardChestCard): string {
  return `/images/thumb/64/${card.imageFolder}/${card.imageCode ?? ''}.png`;
}
function qtyLabel(card: RewardChestCard): string {
  return card.kind === 'flouz' || card.kind === 'resource'
    ? `×${card.quantity.toLocaleString('fr-FR')}`
    : '';
}

async function openChest(c: RewardChest): Promise<void> {
  if (opening.value !== null) return;
  opening.value = c.id;
  try {
    const res = await store.open(c.id);
    if (!res) { ElMessage.error('Impossible d\'ouvrir le coffre.'); return; }
    resetReveal();
    revealedChestType.value = res.chestType;
    revealedTier.value = res.tier;
    revealNonce.value += 1;
    res.cards.forEach((card, i) => revealCards.push({
      ...card, flipped: false, key: `rc-${revealNonce.value}-${i}`,
    }));
    // Le butin (objets) a rejoint l'inventaire -> rafraîchit le compteur « New ».
    void inventoryNew.refresh();
  } catch {
    ElMessage.error('Impossible d\'ouvrir le coffre.');
  } finally {
    opening.value = null;
  }
}

function flip(card: RevealCard): void { card.flipped = true; }
function flipAll(): void { revealCards.forEach((c) => { c.flipped = true; }); }

/** Enchaîne le coffre suivant (le 1er restant) ou ferme la révélation. */
async function openNext(): Promise<void> {
  const next = store.chests[0];
  if (next) await openChest(next);
}
function backToList(): void { resetReveal(); }
</script>

<template>
  <el-dialog
    v-model="open"
    title="🎁 Récompenses"
    width="min(94vw, 760px)"
    append-to-body
    destroy-on-close
    align-center
    class="overlay-dialog"
  >
    <!-- ============ Révélation (cartes à retourner) ============ -->
    <div v-if="inReveal" class="rc-reveal">
      <div class="rc-reveal-head">
        <img
          v-if="chestImgOk"
          class="rc-chest-img-real"
          :src="CHEST_IMG"
          alt="Coffre de récompense"
          @error="chestImgOk = false"
        />
        <span v-else class="rc-chest-img" aria-hidden="true">🎁</span>
        <div class="rc-reveal-title">{{ revealTitle }}</div>
        <p class="rc-reveal-hint">Clique sur une carte pour la retourner.</p>
      </div>

      <div class="rc-cards">
        <div
          v-for="card in revealCards"
          :key="card.key"
          class="rc-card3d"
          :class="[`rc-rar-${card.rarity}`, { 'is-flipped': card.flipped, 'is-high': card.rarity >= 2, 'is-set': card.component?.setId != null }]"
          @click="flip(card)"
        >
          <div class="rc-card3d-inner">
            <!-- Dos de carte : fond étoilé + logo du jeu au centre ; halo de
                 rareté au survol. -->
            <div class="rc-face rc-face-back">
              <span class="rc-back-stars" aria-hidden="true" />
              <img
                class="rc-back-logo"
                :src="CARD_BACK_LOGO"
                alt=""
                aria-hidden="true"
                v-img-hide-on-error
              />
            </div>

            <!-- Face révélée : image en HAUT + nom ; tooltip d'OBJET standard
                 (ItemTooltipContent, popper sombre lisible) au survol. -->
            <AppInfoCard placement="top" :width="320" :disabled="!card.flipped">
              <div class="rc-face rc-face-front">
                <span v-if="card.flipped && card.rarity >= 2" class="rc-confetti" aria-hidden="true">
                  <i v-for="n in 8" :key="n" :style="{ '--i': n }" />
                </span>
                <img class="rc-front-img" :src="thumb(card)" :alt="card.label" v-img-hide-on-error />
                <span class="rc-front-name">{{ card.label }}</span>
                <span v-if="qtyLabel(card)" class="rc-front-qty text-mono">{{ qtyLabel(card) }}</span>
              </div>
              <template #content>
                <ItemTooltipContent :item="toDisplay(card)" />
              </template>
            </AppInfoCard>
          </div>
        </div>
      </div>

      <div class="rc-reveal-actions">
        <el-button v-if="!allFlipped" text @click="flipAll">Tout retourner</el-button>
        <el-button
          v-if="hasMore"
          type="primary"
          :loading="opening !== null"
          @click="openNext"
        >
          Suivant ({{ store.chests.length }})
        </el-button>
        <el-button @click="backToList">{{ hasMore ? 'Voir mes coffres' : 'Terminer' }}</el-button>
      </div>
    </div>

    <!-- ============ Liste des coffres à ouvrir ============ -->
    <template v-else>
      <el-empty v-if="store.chests.length === 0" description="Aucun coffre à ouvrir pour l'instant." />

      <div v-else class="rc-list" v-loading="store.loading">
        <div
          v-for="c in store.chests"
          :key="c.id"
          class="rc-listrow"
          :class="{ 'is-military': c.chestType === 'Military' }"
        >
          <div class="rc-listrow-main">
            <span class="rc-listrow-icon" :class="{ 'is-shaking': opening === c.id }">🎁</span>
            <div class="rc-listrow-text">
              <span class="rc-listrow-title">{{ chestTitle(c) }}</span>
              <span class="rc-listrow-sub">{{ chestIcon(c) }} {{ c.chestType === 'Military' ? 'Combat' : 'Progression' }}</span>
            </div>
          </div>
          <el-button
            type="primary"
            size="small"
            :loading="opening === c.id"
            :disabled="opening !== null && opening !== c.id"
            @click="openChest(c)"
          >
            Ouvrir
          </el-button>
        </div>
      </div>
    </template>
  </el-dialog>
</template>

<style scoped lang="scss">
.overlay-dialog :deep(.el-dialog__body) {
  max-height: 80vh;
  overflow-y: auto;
  padding-top: 0;
}

// ============ Révélation ============
.rc-reveal-head {
  text-align: center;
  margin-bottom: $space-4;
}
.rc-chest-img {
  font-size: 52px;
  display: inline-block;
  animation: rc-burst 0.6s $easing-default both;
  filter: drop-shadow(0 0 14px rgba($color-warning, 0.6));
}
.rc-chest-img-real {
  width: 96px;
  height: 96px;
  object-fit: contain;
  animation: rc-burst 0.6s $easing-default both;
  filter: drop-shadow(0 0 16px rgba($color-warning, 0.7));
}
@keyframes rc-burst {
  0%   { transform: scale(0.5) rotate(-12deg); opacity: 0.3; }
  55%  { transform: scale(1.3) rotate(8deg); opacity: 1; }
  100% { transform: scale(1) rotate(0); opacity: 1; }
}
.rc-reveal-title {
  font-family: $font-display;
  color: $color-warning;
  font-size: $font-size-lg;
  letter-spacing: 0.04em;
  margin-top: $space-2;
}
.rc-reveal-hint { color: $text-muted; font-size: $font-size-xs; margin: $space-1 0 0; }

.rc-cards {
  display: flex;
  flex-wrap: wrap;
  gap: $space-3;
  justify-content: center;
}

// Carte 3D uniforme (taille fixe -> plus de chevauchement).
.rc-card3d {
  width: 116px;
  height: 156px;
  perspective: 700px;
  cursor: pointer;
}
.rc-card3d-inner {
  position: relative;
  width: 100%;
  height: 100%;
  transition: transform 0.55s $easing-default;
  transform-style: preserve-3d;
}
.rc-card3d.is-flipped .rc-card3d-inner { transform: rotateY(180deg); }

.rc-face {
  position: absolute;
  inset: 0;
  backface-visibility: hidden;
  border-radius: $radius-md;
  border: 2px solid $border-default;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  overflow: hidden;
}
// Dos : espace profond (nébuleuse) + champ d'étoiles + logo centré.
.rc-face-back {
  background:
    radial-gradient(circle at 50% 28%, rgba($color-neon-cyan, 0.18), transparent 55%),
    radial-gradient(circle at 50% 125%, rgba($accent-primary, 0.28), transparent 62%),
    linear-gradient(160deg, #0a1126, #05080f);
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}
// Champ d'étoiles : points blancs répartis (gradients ponctuels, 0 asset).
.rc-back-stars {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image:
    radial-gradient(1.5px 1.5px at 20% 22%, rgba(255, 255, 255, 0.9), transparent),
    radial-gradient(1px 1px at 70% 16%, rgba(255, 255, 255, 0.7), transparent),
    radial-gradient(1.5px 1.5px at 32% 58%, rgba(255, 255, 255, 0.8), transparent),
    radial-gradient(1px 1px at 82% 48%, rgba(255, 255, 255, 0.6), transparent),
    radial-gradient(1px 1px at 14% 82%, rgba(255, 255, 255, 0.7), transparent),
    radial-gradient(1.5px 1.5px at 62% 84%, rgba(255, 255, 255, 0.85), transparent),
    radial-gradient(1px 1px at 90% 78%, rgba(255, 255, 255, 0.55), transparent),
    radial-gradient(1px 1px at 46% 36%, rgba(255, 255, 255, 0.5), transparent);
}
.rc-back-logo {
  position: relative; // au-dessus du champ d'étoiles
  width: 66px;
  height: 66px;
  object-fit: contain;
  opacity: 0.95;
  filter: drop-shadow(0 0 8px rgba($color-neon-cyan, 0.55));
}
// Survol : halo de rareté (intensité ∝ rareté).
.rc-card3d:not(.is-flipped):hover .rc-face-back { transform: translateY(-3px); }
.rc-card3d.rc-rar-0:not(.is-flipped):hover .rc-face-back { box-shadow: 0 0 12px rgba($rarity-common, 0.6); }
.rc-card3d.rc-rar-1:not(.is-flipped):hover .rc-face-back { box-shadow: 0 0 16px rgba($rarity-rare, 0.7); }
.rc-card3d.rc-rar-2:not(.is-flipped):hover .rc-face-back { box-shadow: 0 0 22px rgba($rarity-epic, 0.85); }
.rc-card3d.rc-rar-3:not(.is-flipped):hover .rc-face-back { box-shadow: 0 0 30px rgba($rarity-legendary, 0.95); }

// Face avant (révélée) : contour de rareté. Image en HAUT, nom dessous.
.rc-face-front {
  transform: rotateY(180deg);
  background: $bg-secondary;
  padding: $space-2;
  justify-content: flex-start;
  gap: $space-2;
}
.rc-rar-0 .rc-face-front { border-color: $rarity-common; }
.rc-rar-1 .rc-face-front { border-color: $rarity-rare; }
.rc-rar-2 .rc-face-front { border-color: $rarity-epic; box-shadow: inset 0 0 14px rgba($rarity-epic, 0.35); }
.rc-rar-3 .rc-face-front { border-color: $rarity-legendary; box-shadow: inset 0 0 18px rgba($rarity-legendary, 0.5); }

// Composant de SET : halo de survol + contour VERT, PRIORITAIRES sur la rareté
// (placés après -> gagnent à spécificité égale). Cohérent avec le tooltip vert.
.rc-card3d.is-set:not(.is-flipped):hover .rc-face-back { box-shadow: 0 0 22px rgba($rarity-set, 0.85); }
.is-set .rc-face-front { border-color: $rarity-set; box-shadow: inset 0 0 14px rgba($rarity-set, 0.4); }

.rc-front-img { width: 84px; height: 84px; object-fit: contain; margin-top: 2px; }
.rc-front-name {
  font-size: $font-size-xs;
  color: $text-primary;
  text-align: center;
  line-height: 1.15;
  @include truncate(2);
}
.rc-front-qty { color: $color-warning; font-weight: $font-weight-bold; }

// Confettis (rareté >= Épique) : petites particules qui jaillissent à la révélation.
.rc-confetti {
  position: absolute;
  top: 40%; left: 50%;
  pointer-events: none;
  i {
    position: absolute;
    width: 6px; height: 6px;
    border-radius: 1px;
    background: hsl(calc(var(--i) * 45deg), 90%, 60%);
    animation: rc-confetti 0.7s ease-out both;
    transform: rotate(calc(var(--i) * 45deg)) translateY(0);
  }
}
@keyframes rc-confetti {
  0%   { opacity: 0; transform: rotate(calc(var(--i) * 45deg)) translateY(0) scale(0.5); }
  30%  { opacity: 1; }
  100% { opacity: 0; transform: rotate(calc(var(--i) * 45deg)) translateY(-46px) scale(1); }
}
.rc-card3d.is-high.is-flipped .rc-face-front {
  animation: rc-shine 0.7s ease-out;
}
@keyframes rc-shine {
  0%   { filter: brightness(2.2); }
  100% { filter: brightness(1); }
}

.rc-reveal-actions {
  display: flex;
  justify-content: center;
  gap: $space-2;
  margin-top: $space-4;
}

// ============ Liste des coffres ============
.rc-list { display: flex; flex-direction: column; gap: $space-2; }
.rc-listrow {
  @include flex-between;
  gap: $space-3;
  padding: $space-2 $space-3;
  border-radius: $radius-md;
  background: $bg-secondary;
  border-left: 4px solid $color-neon-cyan;
  &.is-military { border-left-color: $color-warning; }
}
.rc-listrow-main { display: flex; align-items: center; gap: $space-3; }
.rc-listrow-icon {
  font-size: 26px;
  &.is-shaking { animation: rc-shake 0.4s linear infinite; }
}
@keyframes rc-shake {
  0%, 100% { transform: rotate(-8deg); }
  50% { transform: rotate(8deg); }
}
.rc-listrow-text { display: flex; flex-direction: column; line-height: 1.2; }
.rc-listrow-title { font-weight: $font-weight-medium; color: $text-primary; }
.rc-listrow-sub { font-size: $font-size-xs; color: $text-muted; }
</style>
